import BookingModule from "@/store/modules/booking";
import POSConfigModule from "@/store/modules/pos-config";
import UserModule from "@/store/modules/user";
import ReceiptModule from "@/store/modules/receipt";
import { LogService } from "@/services/logging";
import dayjs from "dayjs";
import {
  IBookingReceipt,
  IReceiptPayment,
  IVatToPrint,
  VatLineType,
} from "@/interfaces/receipts";
import { IProductSlotBooking } from "@/types/db/product-slot-booking";
import {
  AccountingState,
  IAccounting,
  IReceiptPaymentDetails,
  PaymentMethods,
  TransactionTypes,
} from "@/types/db/accounting";
import {
  IProductSlotState,
  IQuantifiedProductSlotState,
  PSSType,
  BookingStatus,
} from "@/types/db/product-slot-state";
import { getGroupInfo } from "@/utils/vue-helpers/group";
import { groupedReceiptPSS } from "./product-slot-state";
import { getVats } from "./product-slot-booking";
import { getReceiptPayments } from "@/utils/vue-helpers/accounting";
import { formatDateTime } from "./dates";
import { sumArray } from "./common";
import { groupVatsByRate } from "@/utils/vue-helpers/booking";
import {
  bookingTotalConsideringDiscounts,
  currentOrderTotal,
  restToPay,
  totalAutomaticAddonsDiscounts,
  totalAutomaticAddonsDiscountsInOrder,
  totalDiscountsByPss,
  totalDiscountsByPssInOrder,
} from "@/utils/vue-helpers/new-accountings";
import { getNewestNoneEmptyOrderSession } from "./booking";
import i18n from "@/lang";
const t: (key: string, ...args: unknown[]) => string = (i18n.global as any).t;

/**
 * Whether a ticket was cancelled *within* the given order session. A cancelled
 * ticket keeps the orderSessionId of the session that created it, so its refund
 * accounting is the only link back to the session that cancelled it.
 */
function hasRefundAccountingInOrderSession(
  booking: IProductSlotBooking,
  pssUuid: string,
  orderSessionId: string
): boolean {
  return (booking.accountings || []).some(
    (acc) =>
      acc.orderSessionId === orderSessionId &&
      acc.transactionType === TransactionTypes.TRANSACTION_TYPE_TICKET &&
      acc.refund === 1 &&
      acc.accountingEntriesList.some(
        (ae) => ae.productSlotStateUuid === pssUuid
      )
  );
}

/**
 * Payments, refunds and discounts booked in the given accountings, in cents.
 * Used to undo this session's own payments when working out the balance that
 * was outstanding before it.
 */
function currentOrderPayments(accountings: IAccounting[]): number {
  const paymentAndRefundAccountings = accountings.filter(
    (acc) =>
      ((acc.state === AccountingState.Pending &&
        !acc.id &&
        acc.paymentMethod !== PaymentMethods.METHOD_POS_ADYEN) ||
        (acc.state === AccountingState.Started &&
          !acc.id &&
          acc.paymentMethod !== PaymentMethods.METHOD_POS_ADYEN) ||
        acc.state === AccountingState.Completed) &&
      (acc.transactionType === TransactionTypes.TRANSACTION_TYPE_DISCOUNT ||
        acc.transactionType === TransactionTypes.TRANSACTION_TYPE_PAYMENT ||
        acc.transactionType === TransactionTypes.TRANSACTION_TYPE_REFUND)
  );
  return sumArray(
    paymentAndRefundAccountings.flatMap((acc) =>
      acc.accountingEntriesList.map((ae) => ae.value)
    )
  );
}

export function buildReceiptProducts(
  booking: IProductSlotBooking,
  groupedPSSList: IQuantifiedProductSlotState[],
  vatsToPrint: IVatToPrint[],
  /**
   * When set, the tickets and accountings of a single order session. Discounts
   * are then resolved against that subset rather than the whole booking.
   */
  scope?: { pssList: IProductSlotState[]; accountings: IAccounting[] }
) {
  const pssList = scope?.pssList ?? BookingModule.selectedBooking?.pssList;
  const products = [];

  for (const pss of groupedPSSList) {
    const priceValue = pss.priceValue;
    const groupedPssIds = pss.pssIds;
    let pssGroupDiscountValue = 0;
    for (let i = 0; i < groupedPssIds.length; i++) {
      pssGroupDiscountValue = scope
        ? pssGroupDiscountValue +
          totalDiscountsByPssInOrder(
            groupedPssIds[i],
            scope.pssList,
            scope.accountings
          ) +
          totalAutomaticAddonsDiscountsInOrder(
            scope.pssList,
            scope.accountings,
            groupedPssIds[i]
          )
        : pssGroupDiscountValue +
          totalDiscountsByPss.value(groupedPssIds[i]) +
          totalAutomaticAddonsDiscounts(booking, groupedPssIds[i]);
    }

    const vatIndexes = vatsToPrint
      .filter(
        (line) =>
          line &&
          line.priceId.includes(pss.priceId) &&
          line.type === VatLineType.Purchase
      )
      .map((line) => line?.vatIndex || "")
      .join(",");

    const cancellationVatIndexes = vatsToPrint
      .filter(
        (line) =>
          line &&
          line.priceId.includes(pss.priceId) &&
          line.type === VatLineType.Cancellation
      )
      .map((line) => line?.vatIndex || "")
      .join(",");

    const info = {
      name: pss.categoryName,
      priceName:
        pss.pssType === PSSType[PSSType.Retail] &&
        pss.priceName === pss.categoryName
          ? ""
          : pss.priceName,
      startDateTime: pss.startDateTime ? formatDateTime(pss.startDateTime) : "",
      quantity: pss.quantity,
      priceValue: ((priceValue * pss.quantity) / 100).toFixed(2),
      unitPrice: (priceValue / 100).toFixed(2),
      // A group whose tickets were cancelled nets its discount negative — that
      // is a reversal, not a reduction. The value therefore carries its own
      // sign (the template prints it as-is) and the percentage is always the
      // magnitude, so a reversal reads "50% / 6.00" rather than "-50% / -6.00".
      discountValue:
        pssGroupDiscountValue === 0
          ? ""
          : (-pssGroupDiscountValue / 100).toFixed(2),
      discountPercentage:
        pssGroupDiscountValue === 0
          ? ""
          : Math.abs(
              (pssGroupDiscountValue * 100) / (priceValue * pss.quantity)
            ).toFixed(0),
      hasCancelledTickets: 0,
      quantityCancelled: 0,
      valueCancelled: "0",
      vatIndex: vatIndexes,
      cancellationVatIndex: cancellationVatIndexes,
    };

    const pssObjects = pssList?.filter((ps) =>
      pss.pssIds.join(" - ").includes(String(ps.uuid))
    );
    const cancelledPss = pssObjects?.filter(
      (ps) => ps.bookingStatus === BookingStatus.Canceled
    );
    if (cancelledPss && cancelledPss.length) {
      info.hasCancelledTickets = 1;
      info.quantityCancelled = cancelledPss.length;
      info.valueCancelled = (
        (cancelledPss.length * pss.priceValue) /
        100
      ).toFixed(2);
    }

    products.push(info);
  }

  return products;
}

export async function buildBaseReceiptInfo(
  booking: IProductSlotBooking,
  options: {
    isBill?: boolean;
    /**
     * Renders the receipt for a single order session instead of the whole
     * booking. Belgium requires this: there, a receipt is the fiscal document
     * for one sale, so a later sale on the same booking must not reprint the
     * earlier one's lines.
     */
    orderSessionId?: string | null;
  } = {}
): Promise<IBookingReceipt> {
  const groupInfo = await getGroupInfo();

  const { orderSessionId } = options;

  // Tickets belonging to this session, plus tickets cancelled *in* it: a
  // cancelled ticket keeps the orderSessionId of the session that created it,
  // so it only reaches this receipt through its refund accounting.
  const scopedPssList = orderSessionId
    ? booking.pssList.filter(
        (pss) =>
          pss.orderSessionId === orderSessionId ||
          hasRefundAccountingInOrderSession(
            booking,
            String(pss.uuid),
            orderSessionId
          )
      )
    : booking.pssList;

  const scopedAccountings = orderSessionId
    ? (booking.accountings || []).filter(
        (acc) => acc.orderSessionId === orderSessionId
      )
    : booking.accountings || [];

  const scope = orderSessionId
    ? { pssList: scopedPssList, accountings: scopedAccountings }
    : undefined;

  const groupedPSSList = groupedReceiptPSS(booking, {
    includePending: options.isBill,
    ...(scope
      ? // cancelled and confirmed tickets must not share a group: a cancelled
        // group prints as one negative line
        { pssList: scopedPssList, byBookingStatus: true }
      : {}),
  });

  const total = (
    (scope
      ? currentOrderTotal(scopedPssList, scopedAccountings)
      : bookingTotalConsideringDiscounts.value) / 100
  ).toFixed(2);

  const vatsToPrint = groupVatsByRate(
    getVats(booking, {
      forBill: options.isBill,
      ...(scope
        ? { pssList: scopedPssList, accountings: scopedAccountings }
        : {}),
    }),
    // The whole-booking total excludes cancelled tickets, so their VAT has to
    // be excluded too — counting it made taxTotal too high and netTotal too
    // low. The scoped total takes the other, equally consistent route: it
    // negates cancellations, and vatTotal below negates to match.
    !scope
  );

  const accountings = scopedAccountings;
  let latestPaymentAccounting = null;
  for (let i = 0; i < accountings.length; i++) {
    if (
      accountings[i].transactionType ===
      TransactionTypes.TRANSACTION_TYPE_PAYMENT
    ) {
      if (!latestPaymentAccounting) {
        latestPaymentAccounting = accountings[i];
      } else if (
        dayjs(accountings[i].createdAt).isAfter(
          dayjs(latestPaymentAccounting.createdAt)
        )
      ) {
        latestPaymentAccounting = accountings[i];
      }
    }
  }

  const latestDateTime =
    latestPaymentAccounting?.createdAt || booking.insertDateTime;
  const d = dayjs(latestDateTime);
  const day = formatDateTime(latestDateTime, true);
  const hour = d.format("HH:mm:ss");

  const products = buildReceiptProducts(
    booking,
    groupedPSSList,
    vatsToPrint,
    scope
  );

  const seller = UserModule.user;
  const { firstName, lastName } = seller;

  const payments: IReceiptPayment[] = getReceiptPayments(accountings);

  // When scoped, the total negates cancelled tickets (see currentOrderTotal),
  // so their VAT has to be negated too or the net and tax totals drift.
  const vatTotal = sumArray(
    vatsToPrint.map((vat) => {
      const amount = Number(vat.vatAmount) || 0;
      return scope && vat.type === VatLineType.Cancellation ? -amount : amount;
    })
  );
  const netTotal = Number(total) - vatTotal;

  // Only shown when a session settles a balance without adding any tickets.
  let remainingBalance = 0;
  if (scope && !products.length) {
    remainingBalance =
      (restToPay.value + currentOrderPayments(scopedAccountings)) / 100;
  }

  let paymentDetails: IReceiptPaymentDetails | null = null;
  const adyenPayments = accountings.filter(
    (acc) => acc.paymentMethod === PaymentMethods.METHOD_POS_ADYEN
  );
  if (adyenPayments.length === 1 && adyenPayments[0].paymentDetails) {
    paymentDetails = adyenPayments[0].paymentDetails;
    paymentDetails.card = "*****" + paymentDetails.card;
  }

  return {
    isBill: options.isBill || false,
    products,
    groupInfo,
    bookingRef: booking.reference || booking.uuid || "",
    discountTotal: "",
    total: (Number(total) + remainingBalance).toFixed(2),
    netTotal: netTotal.toFixed(2),
    vat: "",
    vats: vatsToPrint,
    date: day,
    hour: hour,
    currency: booking.currency || "CHF",
    payments: options.isBill ? [] : payments,
    // the session actually rendered, which on a reprint is the copied sale
    sessionId: orderSessionId || undefined,
    remainingBalance: remainingBalance ? remainingBalance.toFixed(2) : "",
    printVatLineLabels: scopedPssList.some(
      (pss) => pss.bookingStatus === BookingStatus.Canceled
    ),
    sellerName: `${(firstName && firstName[0].toUpperCase()) || ""} ${
      (lastName && lastName[0].toUpperCase()) || ""
    }`,
    paymentDetails: options.isBill ? null : paymentDetails,
    taxTotal: vatTotal.toFixed(2),
    orderName: booking.tabName || "",
  };
}

export async function applyReceiptCounter(
  receiptInfo: IBookingReceipt,
  booking: IProductSlotBooking
): Promise<void> {
  const isAutoTestsUser =
    POSConfigModule.groupInfo?.id === 16632 &&
    import.meta.env.VITE_ENV === "test";

  const countryCode = POSConfigModule.getGroupCountryCode;
  const hasThirdPartyReference =
    countryCode === "FR" ||
    countryCode === "IT" ||
    countryCode === "AT" ||
    countryCode === "DE" ||
    countryCode === "BE" ||
    countryCode === "ES";

  if (!isAutoTestsUser && hasThirdPartyReference) {
    return;
  }

  const originalReceiptCounterValue =
    getNewestNoneEmptyOrderSession(booking)?.receiptCounterValue || "";

  // A receipt that was already printed in this order session is a reprint.
  // Mark it as a copy so the COPY label is shown, but do NOT increment the
  // counter — a reprint must not consume a new receipt number (FIS-496).
  if (
    ReceiptModule.receiptCounter &&
    ReceiptModule.receiptCounter.isReceiptPrinted
  ) {
    ReceiptModule.setReceiptAsCopy();
    LogService.info(
      `Receipt counter ${ReceiptModule.receiptCounter.formattedValue} has already been printed; reprinting as a copy without incrementing the counter`,
      { params: ReceiptModule.receiptCounter }
    );
  }

  if (ReceiptModule.isReceiptCopy) {
    // Copy/reprint: keep the ORIGINAL receipt number and show the COPY label.
    receiptInfo.isReceiptCopy = true;
    receiptInfo.copyReceiptLabel = t("becert.copy");
    receiptInfo.orderSessionReference =
      originalReceiptCounterValue ||
      ReceiptModule.receiptCounter?.formattedValue ||
      "";
  } else {
    receiptInfo.orderSessionReference =
      ReceiptModule.receiptCounter?.formattedValue || "";
  }
}
